// The simulator's player pool. Every name here is invented, and obviously so.
//
// Simulated names are obviously made up, so nobody mistakes where the data
// comes from. Some are puns, not all need to be, but every one is plainly
// fake.
//
// So roughly half are football puns and half are simply surnames nobody has
// ever had. A roster of nothing but puns reads as a gag reel and stops looking
// like a screen full of players, which is the other half of what this data is
// for.
//
// That is a correctness rule, not a joke. The simulator is our development and
// walkthrough data — it exists to drive new page designs with real-LOOKING
// information and to reach states real data will not hold still for (a bye, a
// player on injured reserve, a Thursday kickoff, a blowout). The one failure
// mode that ruins it is somebody looking at a screen and not knowing whether
// what they are reading came from nflverse or from here. "Chuck Deepball"
// cannot be mistaken for a data feed. "Patrick Mahomes" can.
//
// The pro team codes stay REAL — the app models the actual league, byes and
// schedule included, and `football.proTeams` validates against those codes.
// It is the people who are made up.
//
// NAME SHAPES ARE DELIBERATE. A pool of tidy seven-letter names tests nothing,
// so this one carries the strings that break layouts: a thirty-four character
// name, a hyphenated first, two apostrophes, a `Jr.` suffix, initials with
// periods, and a couple that are barely four characters. Every one of them is
// somewhere a row has to survive.
//
// `ppg` is a points-per-game baseline in PPR used to seed projections and
// simulated stat lines. Accuracy to any real season is not the point.

export type SimPosition = 'QB' | 'RB' | 'WR' | 'TE' | 'K' | 'DST';

export interface PlayerSeed {
  name: string;
  pos: SimPosition;
  team: string;
  ppg: number;
}

const P = (name: string, pos: SimPosition, team: string, ppg: number): PlayerSeed => ({
  name,
  pos,
  team,
  ppg,
});

export const PLAYER_SEEDS: readonly PlayerSeed[] = [
  // Quarterbacks
  P('Chuck Deepball', 'QB', 'BUF', 23.5),
  P('Lefty Quillfeather', 'QB', 'BAL', 23.8),
  P('Jett Hailmary', 'QB', 'WAS', 22.4),
  P('Bartholomew Vandersnatchington III', 'QB', 'CIN', 21.9),
  P('Ace Spiral', 'QB', 'PHI', 21.6),
  P('Mitt Tanglewood', 'QB', 'KC', 19.8),
  P('Sid Downfield', 'QB', 'TB', 19.9),
  P('Jo Jamboree', 'QB', 'DET', 18.7),
  P('Tuck Bootleg', 'QB', 'DEN', 18.2),
  P('Finn Underfoot', 'QB', 'ARI', 18.4),
  P('Chuck Gunslinger', 'QB', 'SF', 17.9),
  P('Lefty Higgenbotham', 'QB', 'CHI', 17.1),
  P('Jett Pocketts', 'QB', 'MIN', 16.8),
  P('Waldo Doodlebank', 'QB', 'LAC', 17.6),
  P('Ace Audible', 'QB', 'NE', 16.9),
  P('Mitt Hornswoggle', 'QB', 'GB', 17.3),
  P('Sid Snapcount', 'QB', 'HOU', 16.2),
  P('Jean-Luc Pancake', 'QB', 'DAL', 17.0),
  P('Tuck Scramble', 'QB', 'JAX', 15.8),
  P('Finn Whiffletree', 'QB', 'LA', 16.4),
  P('Chuck Kneeldown', 'QB', 'LV', 15.1),
  P('Lefty Crumbworthy', 'QB', 'SEA', 15.6),
  P('Jett Shovelpass', 'QB', 'MIA', 15.9),
  P('Waldo Crankshaw', 'QB', 'CAR', 14.7),
  // Running backs
  P('Ace Cutback', 'RB', 'PHI', 20.1),
  P('Mitt Mirthwaite', 'RB', 'ATL', 19.6),
  P('Sid Scrimmage', 'RB', 'DET', 19.2),
  P('Jo Noodleman', 'RB', 'BAL', 18.1),
  P('Tuck Cutback', 'RB', 'SF', 18.8),
  P('Finn Thistlewood', 'RB', 'LV', 16.4),
  P('Chuck Scrimmage', 'RB', 'GB', 16.2),
  P('Lefty Nettlebaum', 'RB', 'MIA', 16.9),
  P('Jett Cutback', 'RB', 'TB', 15.7),
  P('Waldo Snagglethorn', 'RB', 'IND', 16.0),
  P('Ace Scrimmage', 'RB', 'LA', 15.4),
  P('Mitt Clatterbuck', 'RB', 'CIN', 15.1),
  P('Sid Cutback', 'RB', 'BUF', 14.9),
  P('Jo Pattycake', 'RB', 'NYJ', 14.2),
  P('Tuck Scrimmage', 'RB', 'SEA', 13.8),
  P('Finn Wafflebottom', 'RB', 'LAC', 13.5),
  P('Chuck Cutback', 'RB', 'NO', 13.9),
  P('Lefty Gallowglass', 'RB', 'CAR', 13.1),
  P('Jett Scrimmage', 'RB', 'ARI', 13.3),
  P('Waldo Cobblepot', 'RB', 'NE', 12.4),
  P('Moe Noodleman', 'RB', 'HOU', 12.9),
  P('Mitt Grizzlecomb', 'RB', 'CHI', 12.1),
  P('Shep Thistlewood', 'RB', 'MIN', 12.6),
  P('Jo Scuttlebutt', 'RB', 'JAX', 11.7),
  P('Sal Nettlebaum', 'RB', 'DEN', 11.2),
  P('Finn Quillfeather', 'RB', 'TEN', 11.9),
  P('Dex Snagglethorn', 'RB', 'KC', 11.4),
  P('Lefty Pennywhistle', 'RB', 'DET', 11.8),
  P('D’Andre O’Sackerty', 'RB', 'CLE', 10.9),
  P('Waldo Tanglewood', 'RB', 'PIT', 10.2),
  P('Moe Pattycake', 'RB', 'NYG', 10.6),
  P('Mitt Jamboree', 'RB', 'DAL', 10.1),
  P('Shep Wafflebottom', 'RB', 'ATL', 8.4),
  P('Jo Underfoot', 'RB', 'PIT', 9.7),
  P('Sal Gallowglass', 'RB', 'BUF', 7.2),
  P('Finn Higgenbotham', 'RB', 'CHI', 6.1),
  P('Dex Cobblepot', 'RB', 'LAC', 6.4),
  P('Lefty Doodlebank', 'RB', 'ARI', 7.8),
  P('Flash Grizzlecomb', 'RB', 'JAX', 8.1),
  P('Waldo Hornswoggle', 'RB', 'CAR', 7.5),
  P('Moe Scuttlebutt', 'RB', 'MIN', 8.9),
  P('Mitt Lumpkin', 'RB', 'SEA', 8.6),
  P('Shep Quillfeather', 'RB', 'TB', 7.9),
  P('Jo Whiffletree', 'RB', 'LA', 6.8),
  // Wide receivers
  P('Tuck Catchings', 'WR', 'CIN', 21.4),
  P('Finn Crumbworthy', 'WR', 'MIN', 19.8),
  P('Chuck Gomotion', 'WR', 'DAL', 18.9),
  P('Lefty Crankshaw', 'WR', 'DET', 18.4),
  P('Jett Catchings', 'WR', 'LA', 18.1),
  P('Waldo Mirthwaite', 'WR', 'NYG', 17.2),
  P('Ace Gomotion', 'WR', 'HOU', 16.9),
  P('Mitt Noodleman', 'WR', 'JAX', 16.3),
  P('Sid Catchings', 'WR', 'PHI', 16.6),
  P('Jo Thistlewood', 'WR', 'ATL', 16.0),
  P('Tuck Gomotion', 'WR', 'LAC', 15.2),
  P('Finn Nettlebaum', 'WR', 'CIN', 15.0),
  P('Chuck Catchings', 'WR', 'SEA', 15.4),
  P('Lefty Snagglethorn', 'WR', 'MIA', 15.1),
  P('Jett Gomotion', 'WR', 'NYJ', 14.6),
  P('Waldo Clatterbuck', 'WR', 'ARI', 14.3),
  P('Ace Catchings', 'WR', 'WAS', 14.8),
  P('Mitt Pattycake', 'WR', 'LA', 14.0),
  P('Sid Gomotion', 'WR', 'TB', 14.5),
  P('Jo Wafflebottom', 'WR', 'PIT', 13.7),
  P('Cleatus Fumblewicz Jr.', 'WR', 'KC', 14.1),
  P('Finn Gallowglass', 'WR', 'DEN', 13.2),
  P('Dex Mirthwaite', 'WR', 'BAL', 13.4),
  P('Lefty Cobblepot', 'WR', 'DET', 12.9),
  P('Flash Noodleman', 'WR', 'CHI', 12.6),
  P('Waldo Grizzlecomb', 'WR', 'KC', 12.3),
  P('Moe Thistlewood', 'WR', 'CAR', 12.0),
  P('Mitt Scuttlebutt', 'WR', 'MIN', 12.7),
  P('Shep Nettlebaum', 'WR', 'DAL', 12.4),
  P('Jo Quillfeather', 'WR', 'NO', 12.1),
  P('Sal Snagglethorn', 'WR', 'CLE', 11.6),
  P('Finn Pennywhistle', 'WR', 'LV', 11.8),
  P('Dex Clatterbuck', 'WR', 'JAX', 11.4),
  P('Bo Ng', 'WR', 'CHI', 11.1),
  P('Flash Pattycake', 'WR', 'SF', 11.3),
  P('Waldo Jamboree', 'WR', 'NE', 10.9),
  P('Moe Wafflebottom', 'WR', 'TB', 11.0),
  P('Mitt Underfoot', 'WR', 'TB', 10.4),
  P('Shep Gallowglass', 'WR', 'WAS', 10.6),
  P('Jo Higgenbotham', 'WR', 'GB', 10.2),
  P('Sal Cobblepot', 'WR', 'SEA', 10.1),
  P('Finn Doodlebank', 'WR', 'LAC', 10.3),
  P('Dex Grizzlecomb', 'WR', 'BUF', 9.8),
  P('Lefty Hornswoggle', 'WR', 'IND', 9.4),
  P('Flash Scuttlebutt', 'WR', 'GB', 9.1),
  P('Waldo Lumpkin', 'WR', 'IND', 9.6),
  P('Moe Quillfeather', 'WR', 'MIA', 10.8),
  P('Mitt Whiffletree', 'WR', 'TEN', 9.9),
  P('Shep Pennywhistle', 'WR', 'SF', 8.7),
  P('Jo Crumbworthy', 'WR', 'NYG', 8.9),
  P('Sal Tanglewood', 'WR', 'NO', 8.2),
  P('Finn Crankshaw', 'WR', 'DEN', 7.6),
  P('Dex Jamboree', 'WR', 'ATL', 8.4),
  P('Lefty Mirthwaite', 'WR', 'HOU', 8.0),
  P('Flash Underfoot', 'WR', 'LV', 6.9),
  P('Waldo Noodleman', 'WR', 'NE', 6.6),
  P('Moe Higgenbotham', 'WR', 'MIN', 6.2),
  // Tight ends
  P('Mitt Thistlewood', 'TE', 'LV', 15.2),
  P('Sid Seamsley', 'TE', 'ARI', 14.1),
  P('Jo Nettlebaum', 'TE', 'SF', 12.8),
  P('Tuck Seamsley', 'TE', 'DET', 11.6),
  P('Finn Snagglethorn', 'TE', 'MIN', 10.9),
  P('Chuck Seamsley', 'TE', 'KC', 10.4),
  P('Lefty Clatterbuck', 'TE', 'BAL', 9.9),
  P('Jett Seamsley', 'TE', 'CLE', 9.6),
  P('Waldo Pattycake', 'TE', 'GB', 9.8),
  P('Ace Seamsley', 'TE', 'DEN', 9.1),
  P('Mitt Wafflebottom', 'TE', 'DAL', 8.4),
  P('Shep Crankshaw', 'TE', 'IND', 8.7),
  P('Jo Gallowglass', 'TE', 'CHI', 7.9),
  P('Sal Mirthwaite', 'TE', 'PHI', 8.6),
  P('Finn Cobblepot', 'TE', 'NE', 7.4),
  P('T.J. Blitzwater', 'TE', 'BUF', 7.8),
  P('Lefty Grizzlecomb', 'TE', 'ATL', 7.1),
  P('Flash Thistlewood', 'TE', 'WAS', 6.9),
  P('Waldo Scuttlebutt', 'TE', 'PIT', 6.2),
  P('Moe Nettlebaum', 'TE', 'PIT', 6.5),
  // Kickers
  P('Mitt Quillfeather', 'K', 'DAL', 10.1),
  P('Sid Splitem', 'K', 'DET', 9.3),
  P('Jo Pennywhistle', 'K', 'PIT', 9.0),
  P('Tuck Toepoke', 'K', 'LAC', 8.8),
  P('Finn Tanglewood', 'K', 'MIA', 8.1),
  P('Chuck Kickerson', 'K', 'KC', 8.4),
  P('Lefty Jamboree', 'K', 'BUF', 8.2),
  P('Jett Puntman', 'K', 'DEN', 8.3),
  P('Waldo Underfoot', 'K', 'PHI', 7.9),
  P('Ace Doinkerton', 'K', 'HOU', 8.5),
  P('Mitt Higgenbotham', 'K', 'TB', 8.0),
  P('Sid Shotwell', 'K', 'MIN', 7.8),
  P('Jo Doodlebank', 'K', 'CIN', 7.7),
  P('Maximilian Overthrow-Wigglesworth', 'K', 'ATL', 7.5),
  P('Finn Hornswoggle', 'K', 'WAS', 7.6),
  P('Chuck Toepoke', 'K', 'SEA', 7.9),
  // Team defenses
  P('Roadblocks', 'DST', 'DEN', 9.2),
  P('Brickhouses', 'DST', 'BAL', 8.8),
  P('Doorframes', 'DST', 'PHI', 8.5),
  P('Hardhats', 'DST', 'PIT', 8.3),
  P('Speedbumps', 'DST', 'MIN', 8.6),
  P('Bouncers', 'DST', 'HOU', 8.4),
  P('Gatekeepers', 'DST', 'DET', 7.9),
  P('Turnstiles', 'DST', 'BUF', 7.8),
  P('Padlocks', 'DST', 'KC', 7.6),
  P('Stonewalls', 'DST', 'GB', 7.7),
  P('Bulldozers', 'DST', 'SEA', 7.4),
  P('Crashers', 'DST', 'LAC', 7.3),
  P('Anvils', 'DST', 'SF', 7.1),
  P('Sledges', 'DST', 'TB', 6.9),
  P('Barricades', 'DST', 'CHI', 6.6),
  P('Ironsides', 'DST', 'DAL', 6.4),
  // Depth: the free-agent pool. Lower baselines, still plausible pickups.
  P('Jo Snagglethorn', 'QB', 'CIN', 11.2),
  P('Sal Lumpkin', 'QB', 'LV', 10.8),
  P('Finn Clatterbuck', 'QB', 'SF', 10.5),
  P('Dex Whiffletree', 'QB', 'TEN', 11.0),
  P('Lefty Pattycake', 'RB', 'NYJ', 6.9),
  P('Flash Crumbworthy', 'RB', 'DAL', 6.3),
  P('Waldo Wafflebottom', 'RB', 'JAX', 6.6),
  P('Moe Crankshaw', 'RB', 'NYG', 7.0),
  P('Mitt Gallowglass', 'RB', 'CLE', 5.8),
  P('Shep Mirthwaite', 'RB', 'NO', 5.4),
  P('Jo Cobblepot', 'RB', 'HOU', 5.9),
  P('Sal Noodleman', 'RB', 'TEN', 6.7),
  P('Finn Grizzlecomb', 'RB', 'BAL', 5.6),
  P('Dex Thistlewood', 'RB', 'WAS', 5.2),
  P('Lefty Scuttlebutt', 'RB', 'SF', 6.1),
  P('Flash Nettlebaum', 'RB', 'KC', 5.0),
  P('Waldo Quillfeather', 'RB', 'NO', 5.3),
  P('Moe Snagglethorn', 'RB', 'LA', 5.7),
  P('Mitt Pennywhistle', 'RB', 'IND', 5.1),
  P('Shep Clatterbuck', 'RB', 'GB', 5.5),
  P('Jo Tanglewood', 'RB', 'KC', 6.0),
  P('Sal Pattycake', 'RB', 'CLE', 5.9),
  P('Al Wu', 'RB', 'TB', 4.8),
  P('Dex Wafflebottom', 'RB', 'IND', 4.4),
  P('Lefty Underfoot', 'WR', 'GB', 8.3),
  P('Flash Gallowglass', 'WR', 'TB', 7.4),
  P('Waldo Higgenbotham', 'WR', 'CHI', 7.6),
  P('Moe Cobblepot', 'WR', 'HOU', 7.2),
  P('Mitt Doodlebank', 'WR', 'LAC', 6.8),
  P('Shep Grizzlecomb', 'WR', 'TEN', 6.4),
  P('Jo Hornswoggle', 'WR', 'IND', 7.9),
  P('Sal Scuttlebutt', 'WR', 'LV', 6.1),
  P('Finn Lumpkin', 'WR', 'NE', 6.5),
  P('Dex Quillfeather', 'WR', 'SEA', 5.6),
  P('Lefty Whiffletree', 'WR', 'JAX', 6.7),
  P('Flash Pennywhistle', 'WR', 'CLE', 7.1),
  P('Waldo Crumbworthy', 'WR', 'ARI', 6.9),
  P('Moe Tanglewood', 'WR', 'CIN', 6.2),
  P('Mitt Crankshaw', 'WR', 'PIT', 6.6),
  P('Shep Jamboree', 'WR', 'BUF', 6.8),
  P('Jo Mirthwaite', 'WR', 'NE', 5.9),
  P('Sal Underfoot', 'WR', 'DEN', 6.0),
  P('Finn Noodleman', 'WR', 'NE', 4.9),
  P('Dex Higgenbotham', 'TE', 'BAL', 6.8),
  P('Lefty Thistlewood', 'TE', 'NYJ', 6.1),
  P('Flash Doodlebank', 'TE', 'JAX', 6.4),
  P('Waldo Nettlebaum', 'TE', 'HOU', 6.0),
  P('Moe Hornswoggle', 'TE', 'TB', 5.9),
  P('Mitt Snagglethorn', 'TE', 'NO', 5.5),
  P('Shep Lumpkin', 'TE', 'SEA', 5.2),
  P('Jo Clatterbuck', 'TE', 'CLE', 5.0),
  P('Sal Whiffletree', 'TE', 'NYG', 4.9),
  P('Finn Pattycake', 'TE', 'WAS', 4.2),
  P('Chuck Splitem', 'K', 'JAX', 7.4),
  P('Lefty Wafflebottom', 'K', 'LA', 7.3),
  P('Jett Toepoke', 'K', 'CHI', 7.1),
  P('Waldo Gallowglass', 'K', 'LV', 7.2),
  P('Trapdoors', 'DST', 'LA', 6.2),
  P('Deadbolts', 'DST', 'NYJ', 6.0),
  P('Sandbags', 'DST', 'IND', 5.8),
  P('Portcullis', 'DST', 'ARI', 5.7),
];
